import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PROJECTS, PROJECT_FILTERS } from "@/lib/data";
import ProjectLightbox from "@/components/ProjectLightbox";
import { Seo } from "@/components/Seo";

const CARD_ANIM = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0 },
    transition: { duration: 0.5 },
};

const filterProjects = (filter) =>
    filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.type === filter);

export default function Projects() {
    const [filter, setFilter] = useState("All");
    const [active, setActive] = useState(null); // selected project
    const [idx, setIdx] = useState(0); // lightbox image index
    const location = useLocation();

    const list = useMemo(() => filterProjects(filter), [filter]);

    // Auto-open a project lightbox if ?p=<id> is in the URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("p");
        if (!id) return;
        const proj = PROJECTS.find((p) => p.id === id);
        if (proj) {
            setActive(proj);
            setIdx(0);
        }
    }, [location.search]);

    useEffect(() => {
        if (active) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [active]);

    const open = (p) => {
        setActive(p);
        setIdx(0);
    };

    return (
        <main data-testid="projects-page" className="bg-bone">
            <Seo
                title="Our Projects — Custom Homes & Renovations Central Coast | Brazier Building & Design"
                description="Explore completed custom homes, renovations and extensions by Brazier Building & Design across the Central Coast — North Avoca, Forresters Beach, Bateau Bay, Wamberal and more."
                path="/projects"
            />
            {/* Hero */}
            <section className="relative pt-20 pb-10 md:pt-28 md:pb-16 bg-bone2">
                <div className="container-wide">
                    <div className="overline mb-5">Our work</div>
                    <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight leading-[1.05] max-w-4xl">
                        Projects from across <span className="italic text-clay">the Coast.</span>
                    </h1>
                </div>
            </section>

            {/* Filters */}
            <section className="sticky top-20 md:top-[calc(2.25rem+6rem)] z-30 bg-bone/95 backdrop-blur-md border-b border-line">
                <div className="container-wide py-4 overflow-x-auto">
                    <div className="flex items-center gap-2 min-w-max">
                        {PROJECT_FILTERS.map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`project-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
                                    filter === f
                                        ? "bg-ink text-bone border-ink"
                                        : "bg-transparent text-ink2 border-line hover:border-ink"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="section-pad">
                <div className="container-wide">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        <AnimatePresence mode="popLayout">
                            {list.map((p) => (
                                <motion.button
                                    layout
                                    key={p.id}
                                    {...CARD_ANIM}
                                    onClick={() => open(p)}
                                    data-testid={`project-card-${p.id}`}
                                    className="group text-left"
                                >
                                    <div className="aspect-[4/5] overflow-hidden bg-bone2">
                                        <img
                                            src={p.cover}
                                            alt={p.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="mt-5 flex items-center justify-between gap-4">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                                                {p.suburb}
                                            </h3>
                                        </div>
                                        <span className="text-[10px] tracking-[0.22em] uppercase text-clay border border-clay/40 px-3 py-1 whitespace-nowrap shrink-0">
                                            {p.type}
                                        </span>
                                    </div>
                                </motion.button>
                            ))}
                        </AnimatePresence>
                    </div>

                    {list.length === 0 && (
                        <div className="text-center py-24 text-ink2">No projects in this category yet.</div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            <AnimatePresence>
                {active && (
                    <ProjectLightbox
                        project={active}
                        idx={idx}
                        setIdx={setIdx}
                        onClose={() => setActive(null)}
                    />
                )}
            </AnimatePresence>
        </main>
    );
}
